import { Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
// Store
import { Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, mergeMap, take, takeUntil } from 'rxjs/operators';

import { claimQuery, fromClaimActions } from '@amfam/claim/data-access';
import { ClaimDetailConstant } from '@amfam/claim/detail/feature';
import {
  CALLING_CONTEXT,
  CALLING_SUB_CONTEXT,
  claimEnterpriseQuery,
  ClaimEnterpriseService,
  fromClaimEnterpriseActions
} from '@amfam/claim/enterprise/data-access';
import {
  eligibleServicesQuery,
  fromEligibleServicesActions
} from '@amfam/claim/post-fnol/services-data-access';
import { env } from '@amfam/environment';
import { PolicySelectors } from '@amfam/policy/data-access';
import { AnalyticsActions } from '@amfam/shared/analytics';
import { QuickLink } from '@amfam/shared/ui/quick-links';
import { userQuery } from '@amfam/shared/user';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { WindowRef } from '@amfam/shared/utility/shared-services';
import { DockingBarService, PageTitleService } from '@amfam/ui-kit';

@Component({
  selector: 'ds-fnol-claim',
  templateUrl: './fnol-claim.component.html',
  styleUrls: ['./fnol-claim.component.scss'],
  standalone: false
})
export class FnolClaimComponent implements OnInit, OnDestroy {
  loading$: Observable<boolean>;
  claimStatusEligible: boolean;
  quickLinksList = new Array<QuickLink>();
  claimType: string;
  claimsEnterpriseApp = false;
  claimNumber: string;
  loadingIframe = false;
  initialLoadClaimEnterprise = true;
  isPartner: Observable<boolean>;
  iframeTag = '';
  claimUrl = '';
  isContactBillingToggleEnabled = this.feature.isEnabled('contact_billing');

  claimDetailConstant = ClaimDetailConstant;
  reqType: string;
  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();
  private removeMessageListener: () => void;

  constructor(
    private route: ActivatedRoute,
    private pageTitle: PageTitleService,
    private feature: FeatureFlagService,
    private store: Store,
    private dockingBarService: DockingBarService,
    private claimEnterpriseService: ClaimEnterpriseService,
    private router: Router,
    private win: WindowRef,
    private renderer: Renderer2
  ) {}

  ngOnDestroy() {
    this.removeMessageListener();
    this.stop$.next();
    this.stop$.complete();
  }

  ngOnInit() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.pageTitle.set('Claims');
    this.claimsEnterpriseApp = this.feature.isEnabled('claims_enterprise_app');
    this.isPartner = this.store.select(BrandSelectors.selectIsPartner);

    // Top-level loading spinner for claims
    this.loading$ = combineLatest(
      this.store.select(claimQuery.selectClaimsLoaded),
      this.store.select(claimQuery.selectClaimsAnyLoading),
      this.store.select(eligibleServicesQuery.getEligibleServicesLoading),
      (claimsLoaded, claimsLoading, eligibleServicesLoading) =>
        !claimsLoaded || claimsLoading || eligibleServicesLoading
    );

    // load detail and set heading
    combineLatest(
      this.route.params,
      this.store.select(BrandSelectors.selectIsPartner),
      this.store.select(userQuery.selectCustomerId),
      this.store.select(claimQuery.selectClaimsLoaded),
      (routeParams, isPartner, customerId, claimsLoaded) => ({
        routeParams,
        isPartner,
        customerId,
        claimsLoaded
      })
    )
      .pipe(
        // wait until all claims list has been loaded
        filter(state => state.claimsLoaded),
        takeUntil(this.stop$)
      )
      .subscribe(state => {
        this.reqType = state.routeParams['id'];

        this.store.dispatch(new fromEligibleServicesActions.ResetEligibleServices());

        // set dockingbar heading
        if (this.reqType !== 'fileaclaim' && this.reqType !== 'claim') {
          this.dockingBarService.registerHeading('Claim Details');
        } else if (this.reqType === 'fileaclaim') {
          this.dockingBarService.registerHeading('FILE A CLAIM ');
        } else {
          this.dockingBarService.registerHeading('Overview');
        }

        // set quicklinks
        this.quickLinksList = [
          new QuickLink('Make a Payment', '/billing'),
          new QuickLink('Request a Change', '/policies/changerequest'),
          new QuickLink('Contact a Billing Specialist', '/billing/contact'),
          new QuickLink('FAQs', env.links.amfamDotComClaimsFaqUrl, 'amfam')
        ]
          .filter(
            link =>
              // filter Request a Change link if non-AFI partner
              !(
                (_get(link, 'text') === 'Request a Change' || _get(link, 'text') === 'FAQs') &&
                state.isPartner
              )
          )
          .filter(
            link =>
              // fitler Contact a Billing Specialist link if contact billing is toggled off
              !(
                _get(link, 'text') === 'Contact a Billing Specialist' &&
                !this.isContactBillingToggleEnabled
              )
          );
      });
    if (this.claimsEnterpriseApp) {
      this.loadCliamDetailIframe();

      combineLatest([this.store.select(claimEnterpriseQuery.selectClaimEnterpriseState)])
        .pipe(
          takeUntil(this.stop$),
          map(([state]) => state)
        )
        .subscribe(response => {
          const { url, hasError } = response;
          this.loadingIframe = false;
          if (url && !this.initialLoadClaimEnterprise && this.claimUrl !== url) {
            this.claimUrl = url;
          }
          this.initialLoadClaimEnterprise = false;
          if (hasError) {
            this.claimEnterpriseService.errorPageRedirectPath = '/overview';
            this.claimEnterpriseService.errorPageRedirectBtnText = 'Back to Claims';
            this.router.navigateByUrl('/claims/error');
            if (this.reqType === 'fileaclaim') {
              this.store.dispatch(
                AnalyticsActions.sendAnalytic({
                  options: {
                    page: {
                      pageName: 'MyAccount:FileAClaim:Error',
                      experience: '',
                      primaryCategory: 'My Account',
                      subCategory1: 'Claims',
                      subCategory2: 'Error',
                      subCategory3: ''
                    }
                  }
                })
              );
            }
            this.claimEnterpriseService.errorPageRedirectBtnText = 'Back to Overview';
            this.router.navigateByUrl('/overview/error');
          }
        });
    }

    this.removeMessageListener = this.renderer.listen(window, 'message', event => {
      if (event.origin === 'https://localhost.amfam.com:4200') return;

      if (event.data?.claimNumber) {
        this.claimNumber = event.data.claimNumber;
      }
      switch (event.data?.messageName) {
        case 'FNOL_STOP_SPINNER':
        case 'POST_FNOL_STOP_SPINNER':
          this.loadingIframe = false;
          break;
        case 'FNOL_SESSION_TIMEOUT_ERROR':
          this.loadCliamDetailIframe();
          break;
        case 'FNOL_SCROLL':
          this.win.nativeWindow.scrollTo(0, 0);
          break;
        case 'FNOL_LOAD_SUMMARY':
          this.claimUrl = '';
          this.initialLoadClaimEnterprise = true;
          this.loadCliamNumberDetailIframe(event.data?.claimNumber);
          break;
        case 'FNOL_LOAD_SUMMARY_V2':
          this.claimUrl = '';
          this.initialLoadClaimEnterprise = true;
          this.loadCliamNumberDetailIframe(event.data?.claimNumber, CALLING_CONTEXT.POST_FNOL_V2);
          break;
        case 'FNOL_ACCIDENT_CHECKLIST':
          this.loadCliamDetailIframe();
          break;
        case 'FNOL_TG_SAFELITE':
          this.loadCliamDetailIframe();
          break;
        case 'FNOL_LAUNCH_SAFELITE':
          this.routeToSafelite(event.data?.htmlContent);
          break;
        case 'FNOL_SUBMITTED_POST_FNOL':
          this.loadIframeForFnolSumbission(
            CALLING_CONTEXT.POST_FNOL,
            event.data.claimNumber,
            false
          );
          break;
        case 'FNOL_SUBMITTED_POST_FNOL_V2':
          this.loadIframeForFnolSumbission(
            CALLING_CONTEXT.POST_FNOL_V2,
            event.data.claimNumber,
            true
          );
          break;
        case 'POST_FNOL_GO_TO_CLAIMS':
          this.loadIframeForFnolGotoClaim(CALLING_CONTEXT.FNOL, event.data.policyNumber, true);
          break;
        default:
          break;
      }
    });
  }

  loadIframeForFnolSumbission(
    callingContext: CALLING_CONTEXT,
    claimNumber: string,
    isProperty: boolean
  ) {
    this.store.dispatch(fromClaimActions.SetSelectedClaim(claimNumber));
    this.claimEnterpriseService
      .buildPayload(callingContext, CALLING_SUB_CONTEXT.POST_FNOL, claimNumber)
      .subscribe(payload => {
        this.loadingIframe = true;

        this.store.dispatch(
          fromClaimEnterpriseActions.CreateEnterpriseClaim({ payload, isProperty })
        );
      });
  }

  loadIframeForFnolGotoClaim(
    callingContext: CALLING_CONTEXT,
    claimNumber: string,
    isProperty: boolean
  ) {
    this.store.dispatch(fromClaimActions.SetSelectedClaim(claimNumber));
    this.claimEnterpriseService
      .buildPayload(callingContext, CALLING_SUB_CONTEXT.CLAIMS_OVERVIEW, claimNumber)
      .subscribe(payload => {
        this.loadingIframe = true;

        this.store.dispatch(
          fromClaimEnterpriseActions.CreateEnterpriseClaim({ payload, isProperty })
        );
      });
  }

  public routeToSafelite(htmlContent: string) {
    const newWindow = window.open('', '_self');
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    newWindow.document.write(doc.body.innerHTML);
    newWindow.document.forms[0].submit();
    newWindow.document.close();
  }

  private loadCliamNumberDetailIframe(claimNumber: string, callingContext?: CALLING_CONTEXT) {
    this.claimNumber = claimNumber;
    this.store.dispatch(fromClaimActions.SetSelectedClaim(this.claimNumber));
    this.loadCliamDetailIframe(callingContext);
  }

  private loadCliamDetailIframe(callingContext?: CALLING_CONTEXT) {
    combineLatest([
      this.route.params,
      this.store.select(BrandSelectors.selectIsPartner),
      this.store.select(userQuery.selectCustomerId),
      this.store.select(claimQuery.selectClaimsLoading),
      this.store.select(claimQuery.selectSelectedClaim),
      this.store.select(PolicySelectors.selectPoliciesLoading)
    ])
      .pipe(
        map(([routeParams, isPartner, customerId, claimsLoaded, selectedClaim, policyLoading]) => ({
          routeParams,
          isPartner,
          customerId,
          claimsLoaded,
          selectedClaim,
          policyLoading
        })),
        mergeMap(resp => {
          // if the reqType is neither 'fileaclaim' nor 'claim' then it is the claim number, open iframe with claim detail
          if (this.reqType !== 'fileaclaim' && this.reqType !== 'claim') {
            return this.claimEnterpriseService.buildPayload(
              CALLING_CONTEXT.POST_FNOL,
              CALLING_SUB_CONTEXT.CLAIM_DETAIL,
              this.reqType
            );
          } else {
            return this.claimEnterpriseService.buildPayload(
              this.claimNumber !== undefined
                ? callingContext || CALLING_CONTEXT.POST_FNOL
                : CALLING_CONTEXT.FNOL,
              this.claimNumber !== undefined
                ? CALLING_SUB_CONTEXT.CLAIM_DETAIL
                : this.reqType === 'fileaclaim'
                  ? CALLING_SUB_CONTEXT.RISK_SELECTION
                  : CALLING_SUB_CONTEXT.CLAIMS_OVERVIEW,
              this.claimNumber
            );
          }
        }),
        filter((loading: any) => loading.claimsInfo?.policies.length),
        take(1)
      )
      .subscribe(payload => {
        this.store.dispatch(fromClaimEnterpriseActions.CreateEnterpriseClaim({ payload }));
      });
  }
}
